import { Component, Input, OnInit } from '@angular/core';
import { isDefined } from 'src/app/components/utils/utils';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse, HttpInterceptor } from '@angular/common/http';
import { MaintainService } from '../../maintain.service';
import { ConditionalRowDetailsDTO, FieldConfigDTO, FormColumnConfigDTO } from '../../../../../system/system-dtos';
import { MyErrorStateMatcher } from '../../../../../common/field-validation/my-error-state-matcher';
import { AuthInterceptor } from '../../../../../common/interceptors/auth-interceptor';

@Component({
  selector: 'app-expanded-row',
  templateUrl: './expanded-row.component.html',
  styleUrls: ['./expanded-row.component.scss'],
  standalone: false,
})
export class ExpandedRowComponent implements OnInit {
  @Input() element: any;

  @Input() menuConfig: any;

  @Input() conditional: ConditionalRowDetailsDTO;

  fieldsToDisplay: FieldConfigDTO[];

  editMode: boolean = false;

  loading: boolean = true;

  data: any;

  public formGroup: UntypedFormGroup;

  error: string;

  constructor(
    private maintainService: MaintainService,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.fieldsToDisplay = this.conditional.detailsConfig.columns[0].fields;

    this.data = this.element;
    if (!this.conditional.url) {
      this.handleData(this.data);
      return;
    }
    const url = this.constructUrl();
    this.error = undefined;
    this.maintainService.selectOne(url).subscribe(
      (d) => {
        this.handleData(d);
      },
      (err: HttpErrorResponse) => {
        this.error = AuthInterceptor.doGetErrorMessage(err, 'json');
        if (this.error.toLowerCase().includes('not found')) {
          this.error = 'Not found';
        }
        this.loading = false;
      }
    );
  }

  private handleData(d) {
    this.createFormGroupsIfNeededV2(d);
    this.data = d;
    this.loading = false;
  }

  private constructUrl() {
    let { url } = this.conditional;
    Object.keys(this.element)
      .filter((k) => !!this.element[k])
      .forEach((k) => {
        url = url.replace(`{${k}}`, this.element[k]);
      });
    return url;
  }

  private createFormGroupsIfNeededV2(data: any) {
    if (!this.conditional.grouped) {
      this.createFlatForm(data);
    } else {
      this.createGroupedForm(data);
    }
  }

  private createFlatForm(data: any) {
    const formGroup = new UntypedFormGroup({});
    this.getDisplayableFields().forEach((f: FieldConfigDTO) => {
      const disabled = f.editReadOnly;
      const value = !this.isEmpty(data) ? data[f.name] : null;

      const validators = MyErrorStateMatcher.convertToAngularValidators([f]);
      const formControl = new UntypedFormControl(
        {
          value,
          disabled,
        },
        validators
      );
      formGroup.addControl(f.name, formControl);
    });
    this.formGroup = formGroup;
  }

  private createGroupedForm(data: any) {
    const { columns } = this.conditional.detailsConfig;
    const parentFormGroupBuilder = {};
    columns
      .filter((c) => !!c.code)
      .forEach((c) => {
        if (c.nested) {
          const formGroupBuilder = {};
          c.fields.forEach((f) => {
            const formControl = this.getFormControlForField(f, data, c);
            if (isDefined(formControl)) {
              formGroupBuilder[f.name] = formControl;
            }
          });
          parentFormGroupBuilder[c.code] = new UntypedFormGroup(formGroupBuilder);
        } else {
          const field = c.fields[0];
          const formControl = this.getFormControlForField(field, data, c);
          if (isDefined(formControl)) {
            parentFormGroupBuilder[field.name] = formControl;
          }
        }
      });
    this.formGroup = new UntypedFormGroup(parentFormGroupBuilder);
  }

  private getFormControlForField(f: FieldConfigDTO, data: any, c: FormColumnConfigDTO) {
    if (isDefined(data[c.code])) {
      const disabled = f.editReadOnly;
      const value = !this.isEmpty(data) ? data[c.code][f.name] : null;

      const validators = MyErrorStateMatcher.convertToAngularValidators([f]);

      return new UntypedFormControl(
        {
          value,
          disabled,
        },
        validators
      );
    }
    return undefined;
  }

  isEmpty(data: any) {
    return !data || Object.keys(data).length === 0;
  }

  getDisplayableFields() {
    return this.conditional.detailsConfig.columns
      .map((c) => c.fields)
      .reduce((acc, elem) => acc.concat(elem), [])
      .filter((c) => !c.displayInTable);
  }

  isIgnored(column: FormColumnConfigDTO, data: any) {
    return column.ignorePanel && !data;
  }

  readData(column: FormColumnConfigDTO) {
    return !column.nested && column.fields.length === 1 && column.fields[0].type === 'nested_table'
      ? this.data
      : this.data[column.code];
  }

  refresh() {
    this.loading = true;
    this.ngOnInit();
  }
}
